import React, {ReactNode, Component, ReactElement} from "react"
import {injectIntl, InjectIntl} from "@irati/utils/lang"
import {debounce} from "lodash"
import {Row, Col} from "react-bootstrap"

import {QueryUsersArgs} from "@irati/identity/services/queries/usersForBusiness"
import HR from "@irati/ui/src/Layout/Hr"
import {Body} from "@irati/ui/src/Typography"
import {Select} from "@irati/ui/src/FormItem"
import Spacing from "@irati/ui/src/Spacing"
import {Team} from "@irati/types"
import adminTracking from "@irati-tracking/admin"
import TeamName from "@irati-web/platform/src/components/TeamName"

import Search from "../Search"
import {SORT_OPTIONS} from "../../constants/sort"
import {ALL_PEOPLE, ADMIN, USER, DEACTIVATED_PEOPLE, FILTER_OPTIONS, MANAGER} from "../../constants/filter"

export type onBusinessUnitUpdate = (businessUnitId?: string) => void
export type onSearch = (searchString: string) => void
export type onFilter = (variables: Pick<QueryUsersArgs, "enabled" | "adminsOnly" | "managersOnly">) => void
export type onSort = (sort: string) => void

interface Props extends InjectIntl {
  sortOptions: string[]
  filterOptions: string[]
  variables: QueryUsersArgs
  onSearch: onSearch
  onFilter: onFilter
  onSort: onSort
  onBusinessUnitUpdate: onBusinessUnitUpdate
  viewingText: ReactElement | null
  businessUnits: Team[]
  businessUnitId?: string
  children: ReactNode
}
interface State {
  searching: string
  businessUnitId: string
}
const ALL_BUSINESS_UNITS = "ALL"
class SearchAndSortBusinessUnit extends Component<Props, State> {
  static defaultProps = {
    sortOptions: SORT_OPTIONS,
    filterOptions: FILTER_OPTIONS
  }
  constructor(props: Props) {
    super(props)
    this.state = {
      searching: "",
      businessUnitId: props.businessUnitId || ALL_BUSINESS_UNITS
    }
    this.searchRequest = debounce(this.searchRequest.bind(this), 500)
  }
  searchRequest = () => {
    const {searching} = this.state
    this.props.onSearch(searching)
  }
  handleSearch = (searching: string) => {
    this.setState({searching})
    if (searching?.length >= 3 || !searching) {
      this.searchRequest()
    }
  }
  handleSort = (sort) => {
    this.props.onSort(sort)
  }
  handleFilter = (value) => {
    let enabled = !(value === DEACTIVATED_PEOPLE)
    let adminsOnly = !(value === DEACTIVATED_PEOPLE || value === USER || value === MANAGER)
    let managersOnly = !(value === DEACTIVATED_PEOPLE || value === USER || value === ADMIN)
    if (value === ALL_PEOPLE) {
      enabled = null
      adminsOnly = false
      managersOnly = false
    }
    this.props.onFilter({enabled, adminsOnly, managersOnly})
  }
  getFilterValue = () => {
    const {enabled, adminsOnly, managersOnly} = this.props.variables
    if (enabled === false) {
      return DEACTIVATED_PEOPLE
    }
    if (enabled === null && adminsOnly === false) {
      return ALL_PEOPLE
    }
    if (managersOnly) {
      return MANAGER
    }
    return adminsOnly ? ADMIN : USER
  }

  translateOptions = (options) => {
    return options.map(({label, value}) => {
      return {label: this.props.formatMessage({id: label}), value}
    })
  }

  getBusinessUnitOptions = () => {
    const {formatMessage, businessUnits} = this.props
    const options = businessUnits.map((team) => ({label: <TeamName team={team} />, value: team.uuid}))
    return [{label: formatMessage({id: "admin.components.searchAndSort.allCountries"}), value: ALL_BUSINESS_UNITS}, ...options]
  }

  handleUpdateBusinessUnit = (businessUnitId: string) => {
    this.setState({businessUnitId})
    const teamId = businessUnitId === ALL_BUSINESS_UNITS ? null : businessUnitId
    this.props.onBusinessUnitUpdate(teamId)
  }

  render() {
    const {sortOptions, filterOptions, variables, viewingText, children, formatMessage} = this.props
    const {searching, businessUnitId} = this.state

    return (
      <>
        <Spacing marginBottom={3}>
          <Row>
            <Col sm={12}>
              <Row>
                <Search onChange={this.handleSearch} value={searching} />
              </Row>
            </Col>
            <Col sm={4}>
              <Row onClick={adminTracking.filterRegion}>
                <Select
                  id="adminSection_filterByCountry"
                  label={formatMessage({id: "admin.components.searchAndSort.filters.countries.title"})}
                  isClearable={false}
                  value={businessUnitId}
                  options={this.getBusinessUnitOptions()}
                  onChange={this.handleUpdateBusinessUnit}
                />
              </Row>
            </Col>
            {sortOptions && variables && variables.sort && (
              <Col sm={4}>
                <Row onClick={adminTracking.filterStatusRole}>
                  <Select
                    id="adminSection_filterByRole"
                    label={formatMessage({id: "admin.components.searchAndSort.filters.statusRole.title"})}
                    value={this.getFilterValue()}
                    options={this.translateOptions(filterOptions)}
                    isDisabled={businessUnitId !== ALL_BUSINESS_UNITS}
                    onChange={this.handleFilter}
                  />
                </Row>
              </Col>
            )}
            {filterOptions && (
              <Col sm={4}>
                <Row onClick={adminTracking.filterSortBy}>
                  <Select
                    id="adminSection_filterBySort"
                    label={formatMessage({id: "admin.components.searchAndSort.filters.statusRole.sortBy"})}
                    value={variables.sort}
                    options={this.translateOptions(sortOptions)}
                    onChange={this.handleSort}
                  />
                </Row>
              </Col>
            )}
          </Row>
          {children}
        </Spacing>
        <Row>
          <Col xs={12}>
            {viewingText ? (
              <Body small marginBottom={1}>
                {viewingText}
              </Body>
            ) : (
              <div />
            )}
            <HR />
          </Col>
        </Row>
      </>
    )
  }
}
export default injectIntl(SearchAndSortBusinessUnit)
